import React from 'react';
import Icon from '../../../components/AppIcon';
import { useSelector } from 'react-redux';

const EmployeeStats = ({ employees }) => {
  // console.log(employees);
  const totalEmployees = employees.length;
  const activeEmployees = employees.filter(emp => emp.active === true).length;
  const inactiveEmployees = employees.filter(emp => emp.active !== true).length;
  const suspendedEmployees = employees.filter(emp => emp.status === 'suspended').length;
  
  const totalBillingLimit = employees.reduce((sum, emp) => sum + emp?.LastMonthSale, 0);
  const totalBillingUsed = employees.reduce((sum, emp) => sum + emp.billingUsed, 0);
  
  const departmentCounts = employees.reduce((acc, emp) => {
    const dept = emp.role || 'Unassigned';
    acc[dept] = (acc[dept] || 0) + 1;
    return acc;
  }, {});
  const {totalExpense} = useSelector((state) => state.sale);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const stats = [
    {
      title: 'Total Employees',
      value: totalEmployees,
      icon: 'Users',
      color: 'text-primary bg-primary/10',
      change: '+12%',
      changeType: 'positive'
    },
    {
      title: 'Active Employees',
      value: activeEmployees,
      icon: 'UserCheck',
      color: 'text-success bg-success/10',
      change: '+8%',
      changeType: 'positive'
    },
    {
      title: 'Total Billing ',
      value: formatCurrency(totalBillingLimit),
      icon: 'DollarSign',
      color: 'text-accent bg-accent/10',
      change: '+15%',
      changeType: 'positive'
    },
    {
      title: 'Total Expenses This Month',
      value: totalExpense ? formatCurrency(totalExpense) : formatCurrency(0),
      icon: 'TrendingUp',
      color: 'text-secondary bg-secondary/10',
      change: '+5%',
      changeType: 'positive'
    }
  ];

  return (
    <div className="space-y-6 mb-6">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                <p className="text-2xl font-bold text-foreground mt-2">{stat.value}</p>
                <div className="flex items-center mt-2">
                  <span className={`text-xs font-medium ${
                    stat.changeType === 'positive' ? 'text-success' : 'text-destructive'
                  }`}>
                    {stat.change}
                  </span>
                  <span className="text-xs text-muted-foreground ml-1">vs last month</span>
                </div>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${stat.color}`}>
                <Icon name={stat.icon} size={24} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Employee Status</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 rounded-full bg-success"></div>
                <span className="text-sm text-foreground">Active</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-foreground">{activeEmployees}</span>
                <span className="text-xs text-muted-foreground">
                  ({Math.round((activeEmployees / totalEmployees) * 100)}%)
                </span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 rounded-full bg-warning"></div>
                <span className="text-sm text-foreground">Inactive</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-foreground">{inactiveEmployees}</span>
                <span className="text-xs text-muted-foreground">
                  ({Math.round((inactiveEmployees / totalEmployees) * 100)}%)
                </span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 rounded-full bg-destructive"></div>
                <span className="text-sm text-foreground">Suspended</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-foreground">{suspendedEmployees}</span>
                <span className="text-xs text-muted-foreground">
                  ({Math.round((suspendedEmployees / totalEmployees) * 100)}%)
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Department Distribution */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Department Distribution</h3>
          <div className="space-y-3">
            {Object.entries(departmentCounts)
              .sort(([,a], [,b]) => b - a)
              .slice(0, 5)
              .map(([department, count]) => (
                <div key={department} className="flex items-center justify-between">
                  <span className="text-sm text-foreground capitalize">{department}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${(count / totalEmployees) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-foreground w-8 text-right">{count}</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeStats;